import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Rss } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import useReveal from "@/hooks/useReveal";
import { INSIGHTS } from "@/data/content";

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "id" ? "id-ID" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

export default function Insights() {
  const ref = useRef(null);
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [hover, setHover] = useState(0);

  useReveal(ref, () => {
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='i-eyebrow']", { opacity: 0, y: 20, duration: 0.7 });
      gsap.from("[data-anim='i-title']", { opacity: 0, y: 40, duration: 1, ease: "power3.out", delay: 0.1 });
      gsap.from("[data-anim='i-row']", {
        opacity: 0,
        y: 30,
        duration: 0.85,
        stagger: 0.07,
        ease: "power3.out",
        delay: 0.3,
      });
    }, ref);
    return () => ctx.revert();
  });

  const active = INSIGHTS[hover] || INSIGHTS[0];

  return (
    <div
      ref={ref}
      className="relative w-full h-screen overflow-hidden grain flex flex-col px-6 sm:px-8 md:px-16 lg:px-24 pt-36 md:pt-32 pb-10"
      style={{ background: "var(--ink)" }}
      data-testid="insights-root"
    >
      {/* faint editorial column grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(90deg, transparent calc(100% - 1px), white calc(100% - 1px))",
          backgroundSize: "12.5% 100%",
        }}
      />
      {/* accent halo following active row */}
      <div
        aria-hidden
        className="absolute pointer-events-none rounded-full transition-all duration-[1200ms] ease-out"
        style={{
          width: 900,
          height: 900,
          top: "-18%",
          right: "-20%",
          background: "radial-gradient(circle, #5b4bff 0%, transparent 60%)",
          opacity: 0.16,
          filter: "blur(40px)",
        }}
      />

      {/* Top eyebrow */}
      <div
        data-anim="i-eyebrow"
        className="relative z-10 flex items-end justify-between mb-8 md:mb-12 border-b border-white/15 pb-5"
      >
        <div className="flex items-baseline gap-4 md:gap-6">
          <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.32em] text-white/55">
            {t("insights.eyebrow")} · 07
          </span>
          <span className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.32em] text-white/55 hidden md:inline">
            {lang === "id" ? "Catatan editorial · Bidang & disiplin" : "Editorial notes · fields & disciplines"}
          </span>
        </div>
        <a
          href="/api/insights/rss.xml"
          target="_blank"
          rel="noreferrer"
          data-testid="insights-rss-link"
          className="inline-flex items-center gap-2 font-mono text-[0.6rem] uppercase tracking-[0.28em] text-white/55 hover:text-[var(--green)] transition-colors"
        >
          <Rss size={12} /> RSS
        </a>
      </div>

      {/* Editorial split — title left, list right */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 flex-1 min-h-0">
        {/* LEFT — sticky title + spotlight excerpt */}
        <div className="lg:col-span-5 flex flex-col justify-between min-h-0">
          <h2
            data-anim="i-title"
            className="font-display text-white"
            style={{
              fontSize: "clamp(1.85rem, 3.6vw, 3.2rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.025em",
              fontWeight: 600,
            }}
          >
            {t("insights.title")}
          </h2>

          {/* Spotlight excerpt — driven by hovered/auto row */}
          <div className="hidden lg:block border-t border-white/12 pt-6 mt-6 max-w-[28rem]">
            <div className="font-mono text-[0.55rem] uppercase tracking-[0.3em] text-white/40 mb-3">
              {lang === "id" ? "Sorotan" : "Spotlight"} · /{String(hover + 1).padStart(2, "0")}
            </div>
            <p
              key={active.slug + "-ex"}
              className="text-white/75 text-[15px] leading-relaxed fade-swap"
            >
              {active.excerpt[lang]}
            </p>
            <div className="mt-5 flex items-center gap-3 font-mono text-[0.58rem] uppercase tracking-[0.28em] text-white/55">
              <span>{active.category[lang]}</span>
              <span className="inline-block w-1 h-1 rounded-full bg-white/30" />
              <span>{active.readingTime} {t("insights.readingTime")}</span>
              <span className="inline-block w-1 h-1 rounded-full bg-white/30" />
              <span>{formatDate(active.date, lang)}</span>
            </div>
          </div>
        </div>

        {/* RIGHT — editorial list */}
        <ol className="lg:col-span-7 flex flex-col justify-start min-h-0 overflow-y-auto pr-1 border-t border-white/12">
          {INSIGHTS.map((p, i) => {
            const isActive = i === hover;
            return (
              <li
                key={p.slug}
                data-anim="i-row"
                className="border-b border-white/12"
              >
                <button
                  type="button"
                  data-testid={`insight-card-${p.slug}`}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onClick={() => navigate(`/insights/${p.slug}`)}
                  className="group w-full flex items-start justify-between gap-5 md:gap-7 py-5 md:py-6 text-left transition-colors"
                >
                  <div className="flex items-start gap-5 md:gap-7 min-w-0 flex-1">
                    <span
                      className="font-mono text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.3em] flex-none pt-1.5 transition-colors"
                      style={{
                        color: isActive ? "var(--green)" : "rgba(255,255,255,0.35)",
                      }}
                    >
                      /{String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3
                        className="font-display leading-tight transition-all duration-500 text-white"
                        style={{
                          fontSize: "clamp(1rem, 1.7vw, 1.55rem)",
                          letterSpacing: "-0.018em",
                          fontWeight: 600,
                          color: isActive ? "#ffffff" : "rgba(255,255,255,0.72)",
                          transform: isActive ? "translateX(6px)" : "translateX(0)",
                        }}
                      >
                        {p.title[lang]}
                      </h3>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[0.55rem] uppercase tracking-[0.28em] text-white/40">
                        <span style={{ color: isActive ? "var(--green)" : "rgba(255,255,255,0.45)" }}>
                          {p.category[lang]}
                        </span>
                        <span className="inline-block w-1 h-1 rounded-full bg-white/25" />
                        <span>{formatDate(p.date, lang)}</span>
                        <span className="inline-block w-1 h-1 rounded-full bg-white/25" />
                        <span>{p.readingTime} {t("insights.readingTime")}</span>
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight
                    size={18}
                    className="flex-none mt-2 transition-all duration-500"
                    style={{
                      color: isActive ? "var(--green)" : "rgba(255,255,255,0.35)",
                      transform: isActive
                        ? "translate(0,0) rotate(0deg)"
                        : "translate(-6px,6px) rotate(-30deg)",
                    }}
                  />
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Bottom rule */}
      <div className="relative z-10 mt-6 flex items-center justify-between font-mono text-[0.55rem] md:text-[0.6rem] uppercase tracking-[0.28em] text-white/45">
        <span>{lang === "id" ? "Editorial · Studio Makario" : "Editorial · Makario studio"}</span>
        <span>
          {String(INSIGHTS.length).padStart(2, "0")} {lang === "id" ? "ARTIKEL" : "ARTICLES"}
        </span>
      </div>

      <style>{`
        .fade-swap { animation: insightFade 600ms ease-out both; }
        @keyframes insightFade {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
